import { Component, QueryList, ViewChildren } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { RoutingService } from '../../services/routing.service';
import { GlobalRecordService } from '../../services/global-record.service';
import { CartService } from '../../cart/services/cart.service';
import { SearchService } from '../../search/services/search.service';
import { MapService } from '../../map/services/map.service';
import { MetadataQuickViewComponent } from '../../views/metadata-quick-view/metadata-quick-view.component';
import { RecordQuickViewComponent } from '../../records-management/components/record-quick-view/record-quick-view.component';
import { TagsComponent } from '../../components/tags/tags.component';
import { DocViewerFactoryService } from '../../services/factory/doc-viewer-factory.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

/**
 * Thumbnail search results view.
 */
@Component({
	selector: 'thumbnail-view',
	templateUrl: './thumbnail-view.component.html',
	styleUrl: './thumbnail-view.component.css'
})
export class ThumbnailViewComponent implements OnInit, OnDestroy {
	@ViewChildren(NgbDropdown) dropdowns!: QueryList<NgbDropdown>;
	
	private userEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;
	private routingEventSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	viewType?: string; // Corresponds to $stateParams.v

	records: any[] = [];

	validFields: any[] = [];
	searchableResultFields: any[] = [];

	visited: { [key: string]: any } = {};
	tagsEnabled: boolean = false;
	cartOn: boolean = false;
	mapViewEnabled: boolean = false;
	additionalIcons: boolean = false;
	isMoreLikeThisEnabled: boolean = false;
	streamingQueries: { [key: string]: any } = {};

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private configService: ConfigService,
		private cartService: CartService,
		public globalRecordService: GlobalRecordService,
		private routingService: RoutingService,
		private searchService: SearchService,
		private mapService: MapService,
		private docViewerFactory: DocViewerFactoryService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'loggedin') {
				this.processSearchResults();
				this.checkCartButtons();
			}
			else if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'searchEnded') {
				this.processSearchResults();
			}
			else if (event.type === 'clearSearch') {
				this.records = [];
			}
		});

		// Listen for route changes, specifically related to the view type
		this.routingEventSubscription = this.routingService.routingEvent.subscribe(event => {
			if (event.eventType === 'navigationEnded') {
				this.processSearchResults();
			}
		});

		this.searchableResultFields = this.configService.getConfig().search.searchableResultFields;
		this.mapViewEnabled = this.configService.getConfig().esri.enabled;
		this.tagsEnabled = this.configService.getConfig()?.menu?.tags;
		this.isMoreLikeThisEnabled = this.configService.getConfig().search.isMoreLikeThis;
		this.streamingQueries = this.configService.getConfig().search.streamingQueries;

		this.visited = this.userService.getLocalStorageData().visited;
		let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name == "DISPLAY_ADDITIONAL_ICONS"; });
		this.additionalIcons = property?.value === "true" ? true : false;

		this.checkCartButtons();
		this.processSearchResults();
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.routingEventSubscription) {
			this.routingEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}
	}

	// TODO
	//	 this.$on("scrollToRecord", function (evt, data) {
	//		$location.hash("thumbnail_" + data.uuid);
	//		$anchorScroll.yOffset = 60;
	//		$anchorScroll();
	//	 });	

	private checkCartButtons(): void {
		if (this.userPermissions['ROLE_PRODUCT_DOWNLOAD_RESTRICTED'] && !this.userPermissions["ROLE_PRODUCT_DOWNLOAD"]) {
			this.cartOn = true;
		} else {
			this.cartOn = false;
		}
	}

	private processSearchResults(): void {
		this.viewType = this.routingService.getQueryParam('v');
		if (!this.viewType || this.viewType !== 'T') {
			return;
		}
        setTimeout(() => {
		  this.scrollToTop();
		}, 0);
		console.log('Processing search results!');

		let results = this.searchService.results;

		if (!results || results.error) {
			this.records = [];
			return;
		}

		if (this.validFields.length == 0) {
			if (!this.userService.user.properties) return;
			this.getValidFields();
		}

		this.records = this.searchService.processSearchResults(results, this.validFields);
	}

	private getValidFields(): void {
		this.validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => {
			return x.isAvailable && (x.isSearch || x.isResult);
		});
	}

	/**
	 * Displays the product in the document viewer.
	 */
	openRecord(id: number): void {
		const viewerComponent = this.docViewerFactory.getViewerComponent();
		const modalRef = this.ngbModal.open(viewerComponent, {
			windowClass: "viewer-modal-window"
		});
		
		modalRef.componentInstance.productId = id;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);

		let userData = this.userService.getLocalStorageData();
		this.visited = userData.visited;
	}

	/**
	 * Displays the full metadata for the product.
	 */
	viewMetadata(prodid: number): void {
		this.closeAllDropdowns();
		const modalRef = this.ngbModal.open(MetadataQuickViewComponent);
		modalRef.componentInstance.productId = prodid;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the record management metadata for the product.
	 */
	viewRMQuickInfo(prodid: number): void {
		this.closeAllDropdowns();
		const modalRef = this.ngbModal.open(RecordQuickViewComponent);
		modalRef.componentInstance.productId = prodid;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the tag manager for the product.
	 */
	viewTags(prodid: number, idx: number): void {
		this.closeAllDropdowns();
		const modalRef = this.ngbModal.open(TagsComponent);
		modalRef.componentInstance.product = {
			id: prodid,
			title: this.records[idx].title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the product in the map view.
	 */
	openInMap(record: any): void {
		this.closeAllDropdowns();
		this.mapService.openInMap(record, false);
	}

	toggleRecordId(id: number): void {
		let currentValue = this.globalRecordService.getRecord(id);
		this.globalRecordService.setRecord(id, !currentValue, true);
	}

	/**
	 * Executes a search for documents similar to this one.
	 */
	searchSimilarDocs(product: any): void {
		this.closeAllDropdowns();
		this.searchService.similaritySearch(product.id, product.title);
	}

	searchByStreamingQuery(product: any, streamingQueryType: string, displayName: string): void {
		this.closeAllDropdowns();
		this.searchService.sreamingQuerySearch(product.id, product.title, streamingQueryType, displayName);
	}

	addToCart(record: any): void {
		this.closeAllDropdowns();
		this.cartService.addToCart(record.id, record.title);
	}
	
	scrollToTop() {
	  const element = document.querySelector('.thumbsHolder');
	  if (element) {
	    element.scrollTop = 0
	  }
	}
	closeAllDropdowns(): void {
    	this.dropdowns.forEach(dropdown => dropdown.close());
    }
}